import React, { useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { stopEvent } from '@aytm/lib/common/func'

import '@aytm/lib/rfd-ui/styles/basic/radio-button.scss'

RadioButton.propTypes = {
  /**
   * is checked
   */
  checked: PropTypes.bool,
  /**
   * is disabled
   */
  disabled: PropTypes.bool,
  /**
   * error state
   */
  error: PropTypes.bool,
  /**
   * Option label
   */
  label: PropTypes.string,
  /**
   * radio button size in pixels
   */
  checkSize: PropTypes.number,
  /**
   * label size in pixels
   */
  labelSize: PropTypes.number,
  /**
   * lineHeight for check and label
   */
  lineHeight: PropTypes.number,
  /**
   * custom style
   */
  style: PropTypes.object,
  /**
   * custom className
   */
  className: PropTypes.string,
  /**
   * "checked" icon className
   */
  checkedIcon: PropTypes.string,
  /**
   * "unchecked" icon className
   */
  uncheckedIcon: PropTypes.string,
  /**
   * fires when RadioButton is `clicked` and `not disabled`: `onAction(event)`
   */
  onAction: PropTypes.func,
  /**
   * fires when RadioButton is `clicked` (disabled or not): `onClick(event)`
   */
  onClick: PropTypes.func,
  /**
   * onKeyDown callback
   */
  onKeyDown: PropTypes.func,
  /**
   * `SPACE` and `ENTER` keys presses works as clicks
   */
  spaceEnterAsClick: PropTypes.bool,
  /**
   * `UP` and `DOWN` key presses will move focus between RadioButtons
   */
  upDownAsMoveFocus: PropTypes.bool,
  /**
   * RadioButton group name for `UP` and `DOWN` keys presses navigation
   */
  groupName: PropTypes.string
}
RadioButton.defaultProps = {
  checkedIcon: 'icon-aytmfd-afdradio-full',
  uncheckedIcon: 'icon-aytmfd-afdradio',
  spaceEnterAsClick: true,
  upDownAsMoveFocus: false,
  groupName: 'default'
}
export default function RadioButton({
  checked,
  disabled,
  error,
  label,
  checkSize,
  labelSize,
  lineHeight,
  style,
  className,
  checkedIcon = 'icon-aytmfd-afdradio-full',
  uncheckedIcon = 'icon-aytmfd-afdradio',
  onAction,
  onClick: onCustomClick,
  onKeyDown: onCustomKeyDown,
  spaceEnterAsClick = true,
  upDownAsMoveFocus = false,
  groupName = 'default',
  ...otherProps
} = {}) {
  const commonLineHeight = useMemo(
    () =>
      lineHeight
        ? `${lineHeight}px`
        : (() => {
            const maxSize = Math.max(checkSize, labelSize)
            return maxSize ? `${maxSize * 1.2}px` : null
          })(),
    [lineHeight, checkSize, labelSize]
  )
  const applyStyle = useMemo(() => (commonLineHeight ? { lineHeight: commonLineHeight } : {}), [commonLineHeight])
  const mergedStyle = useMemo(
    () => (null != style ? Object.assign(applyStyle, style) : applyStyle),
    [style, applyStyle]
  )

  const onClick = useCallback(
    event => {
      if (onCustomClick) {
        onCustomClick(event)
      }
      if (!disabled && (void 0 === event.button || 0 === event.button) && onAction) {
        onAction(event)
      }
    },
    [onCustomClick, onAction, disabled]
  )

  const onKeyDown = useCallback(
    event => {
      if (onCustomKeyDown) {
        onCustomKeyDown(event)
      }
      if (!disabled) {
        if (spaceEnterAsClick && onClick && (' ' === event.key || 'Enter' === event.key)) {
          onClick(event)
          stopEvent(event)
        } else if (upDownAsMoveFocus) {
          if ('ArrowUp' === event.key) {
            const activeRadioButtons = document.querySelectorAll(
              `${groupName ? `.rfd-ui-radiobutton-group-${groupName}` : ''}.rfd-ui-radiobutton[tabindex="0"]`
            )
            for (let i = 0; i < activeRadioButtons.length; i++) {
              if (document.activeElement === activeRadioButtons[i]) {
                if (i > 0) {
                  const prevRadioButtons = activeRadioButtons[i - 1]
                  if (prevRadioButtons) {
                    prevRadioButtons.focus()
                    prevRadioButtons.click()
                  }
                }
                break
              }
            }
            stopEvent(event)
          } else if ('ArrowDown' === event.key) {
            const activeRadioButtons = document.querySelectorAll(
              `${groupName ? `.rfd-ui-radiobutton-group-${groupName}` : ''}.rfd-ui-radiobutton[tabindex="0"]`
            )
            for (let i = 0; i < activeRadioButtons.length; i++) {
              if (document.activeElement === activeRadioButtons[i]) {
                if (i < activeRadioButtons.length - 1) {
                  const nextRadioButtons = activeRadioButtons[i + 1]
                  if (nextRadioButtons) {
                    nextRadioButtons.focus()
                    nextRadioButtons.click()
                  }
                }
                break
              }
            }
            stopEvent(event)
          }
        }
      }
    },
    [onCustomKeyDown, onClick, disabled, spaceEnterAsClick, upDownAsMoveFocus, groupName]
  )

  return (
    <span
      className={`rfd-ui-radiobutton${className ? ' ' + className : ''}${checked ? ' rfd-ui-radiobutton-checked' : ''}${
        disabled ? ' rfd-ui-radiobutton-disabled' : ''
      }${error ? ' rfd-ui-radiobutton-error' : ''}${groupName ? ` rfd-ui-radiobutton-group-${groupName}` : ''}`}
      tabIndex={disabled ? -1 : 0}
      style={mergedStyle}
      onClick={onClick}
      onKeyDown={onKeyDown}
      role="radio"
      aria-checked={checked}
      aria-disabled={disabled ? true : null}
      {...otherProps}
    >
      <i
        className={`rfd-ui-radiobutton-icon ${checked ? checkedIcon : uncheckedIcon}`}
        style={{ fontSize: checkSize, lineHeight: commonLineHeight }}
        role="presentation"
      />
      {label && (
        <span
          className="rfd-ui-radiobutton-label"
          dangerouslySetInnerHTML={{ __html: label }}
          style={{ fontSize: labelSize, lineHeight: commonLineHeight }}
        />
      )}
    </span>
  )
}
